import { useEffect, useState, useCallback, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAppStore } from './store/appStore';
import { useModalStore } from './store/modalStore';
import { dataRepository } from './repository/DataRepository';
import { eventBus } from './events/EventBus';
import { initializeStorageBucket } from './lib/storage';
import { notificationService } from './services/NotificationService';
import { startTaskNotificationReconciler } from './services/taskNotificationReconciler';
import { autoExportService } from './services/autoExportService';
import { autoEmbedQueue } from './services/ai/AutoEmbedQueue';
import { oauthTokenManager } from './services/oauthTokenManager';
import { normalizePriorityValues } from './utils/normalizePriorityValues';
import { setSentryUser } from './lib/sentry';
import Sidebar from './components/Sidebar';
import Footer from './components/Footer';
import CelebrationOverlay from './components/adhd/CelebrationOverlay';
import AuthStatus, { getUserFromStorage } from './components/AuthStatus';
import { supabase } from './lib/supabase';
import './App.css';

// Lazy-loaded page components
const ObjectView = lazy(() => import('./pages/ObjectView'));
const ObjectModal = lazy(() => import('./components/ObjectModal'));
const TaskModal = lazy(() => import('./components/TaskModal'));
const Admin = lazy(() => import('./pages/Admin'));
const Import = lazy(() => import('./pages/Import'));
const Migration = lazy(() => import('./pages/Migration'));
const ProcessingCenter = lazy(() => import('./pages/ProcessingCenter'));
const DailyNotes = lazy(() => import('./pages/DailyNotes'));
const AllTasks = lazy(() => import('./pages/AllTasks'));
const AllProjects = lazy(() => import('./pages/AllProjects'));
const AllMaps = lazy(() => import('./pages/AllMaps'));
const AllAreas = lazy(() => import('./pages/AllAreas'));
const AllNotes = lazy(() => import('./pages/AllNotes'));
const AllSources = lazy(() => import('./pages/AllSources'));
const ReadingList = lazy(() => import('./pages/ReadingList'));
const AllArchive = lazy(() => import('./pages/AllArchive'));
const HabitsPage = lazy(() => import('./pages/HabitsPage'));
const AllObjects = lazy(() => import('./pages/AllObjects'));
const TagsView = lazy(() => import('./pages/TagsView'));
const CollectionsPage = lazy(() => import('./pages/CollectionsPage'));
const MarkdownPage = lazy(() => import('./pages/MarkdownPage'));
const PDFPage = lazy(() => import('./pages/PDFPage'));
const GoogleOAuthCallback = lazy(() => import('./pages/GoogleOAuthCallback'));
const GmailOAuthCallback = lazy(() => import('./pages/GmailOAuthCallback'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const AISearch = lazy(() => import('./pages/AISearch'));
const Chat = lazy(() => import('./pages/Chat'));
const Mdm = lazy(() => import('./pages/Mdm'));
const MdmLevel = lazy(() => import('./pages/MdmLevel'));
const GraphView = lazy(() => import('./pages/GraphView'));
const LogoDemo = lazy(() => import('./pages/LogoDemo'));

// Component to handle modal type detection
function ModalRenderer({ objectId, onClose }: { objectId: string; onClose: () => void }) {
  const [objectType, setObjectType] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadObjectType = async () => {
      try {
        const obj = await dataRepository.getObject(objectId);
        if (obj) {
          setObjectType(obj.type);
        }
      } catch (error) {
        console.error('Error loading object type:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadObjectType();
  }, [objectId]);

  if (isLoading) {
    return null; // Brief loading state
  }

  // Use TaskModal for tasks, ObjectModal for everything else
  if (objectType === 'task') {
    return <TaskModal objectId={objectId} onClose={onClose} />;
  }

  return <ObjectModal objectId={objectId} onClose={onClose} />;
}

function App() {
  const { setTopLevelObjects } = useAppStore();
  const { openObjectId, closeObjectModal } = useModalStore();
  const [isAuthenticated, setIsAuthenticated] = useState(() => !!getUserFromStorage());

  // Listen for auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAuthenticated(!!session?.user);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Prevent browser zoom from trackpad gestures globally
  useEffect(() => {
    const preventZoom = (e: WheelEvent) => {
      if (e.ctrlKey) {
        e.preventDefault();
      }
    };

    // Prevent zoom with passive: false to allow preventDefault
    document.addEventListener('wheel', preventZoom, { passive: false });

    return () => {
      document.removeEventListener('wheel', preventZoom);
    };
  }, []);

  // Initialize sync engine on mount
  useEffect(() => {
    // Start singletons synchronously (not affected by async failures below)
    autoEmbedQueue.start();

    const initialize = async () => {
      try {
        // Load top-level objects
        const objects = await dataRepository.getTopLevelObjects();
        setTopLevelObjects(objects);

        // Note: Sync engine is initialized by auth state change callback in SyncEngine.ts
        // No need to call it here

        // Initialize storage bucket for media files
        await initializeStorageBucket();

        // Initialize notification service
        await notificationService.initialize();

        // Schedule browser notifications for tasks with due dates
        startTaskNotificationReconciler();

        // Initialize reminder service (polls for due reminders)
        const { reminderService } = await import('./services/ReminderService');
        reminderService.initialize();

        // Initialize auto-export service
        autoExportService.initialize();

        // Start OAuth token health monitoring (proactive refresh)
        oauthTokenManager.start();

        // Set user context for Sentry
        const userId = dataRepository.getUserId();
        setSentryUser(userId);

        // One-shot repair for tasks prioritized before the priority-buttons
        // fix (2026-08-14): stored 'urgent' where 'critical' is the only
        // value the rest of the app matches on. Gated per-device so it
        // doesn't re-queue sync changes on every launch.
        const NORMALIZE_PRIORITY_KEY = 'pkm:normalizedPriorityValues:v1';
        if (!localStorage.getItem(NORMALIZE_PRIORITY_KEY)) {
          try {
            const result = await normalizePriorityValues();
            if (!result.errors.length) {
              localStorage.setItem(NORMALIZE_PRIORITY_KEY, new Date().toISOString());
            }
            if (result.fixed > 0) {
              console.log(`[App] Repaired ${result.fixed} legacy priority value(s)`);
            }
          } catch (error) {
            console.error('[App] Priority normalization failed:', error);
          }
        }
      } catch (error) {
        console.error('Error initializing app:', error);
      }
    };

    initialize();

    // Refresh sidebar when objects are created, updated, or deleted (debounced)
    let sidebarRefreshTimeout: ReturnType<typeof setTimeout> | null = null;
    const refreshSidebar = () => {
      if (sidebarRefreshTimeout) clearTimeout(sidebarRefreshTimeout);
      sidebarRefreshTimeout = setTimeout(async () => {
        const objects = await dataRepository.getTopLevelObjects();
        setTopLevelObjects(objects);
      }, 500);
    };

    // Subscribe to events and store unsubscribe functions
    const unsubCreated = eventBus.on('object:created', refreshSidebar);
    const unsubUpdated = eventBus.on('object:updated', refreshSidebar);
    const unsubDeleted = eventBus.on('object:deleted', refreshSidebar);

    // Cleanup on unmount
    // Note: Don't stop singletons here - they should persist across component
    // lifecycles (especially with React Strict Mode double-mounting)
    return () => {
      unsubCreated();
      unsubUpdated();
      unsubDeleted();
      if (sidebarRefreshTimeout) clearTimeout(sidebarRefreshTimeout);
      autoExportService.destroy();
      oauthTokenManager.stop();
      // Don't stop autoEmbedQueue — it's a singleton like syncEngine
    };
  }, [setTopLevelObjects]);

  // Show login screen when not authenticated
  if (!isAuthenticated) {
    return (
      <div style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: 'var(--bg-base, #0f172a)', color: 'var(--text-primary, #e2e8f0)',
      }}>
        <Router>
          <Suspense fallback={null}>
            <Routes>
              {/* OAuth callbacks must remain accessible during auth flow */}
              <Route path="/oauth/google-callback" element={<GoogleOAuthCallback />} />
              <Route path="/oauth/gmail-callback" element={<GmailOAuthCallback />} />
              <Route path="*" element={<AuthStatus variant="fullscreen" />} />
            </Routes>
          </Suspense>
        </Router>
      </div>
    );
  }

  return (
    <Router>
      <Suspense fallback={<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: 'var(--bg-base, #0f172a)', color: 'var(--text-secondary, #94a3b8)' }}>Loading…</div>}>
        <Routes>
          <Route path="/admin" element={<Admin />} />
          <Route path="/queries" element={<Navigate to="/admin" replace />} />
          <Route path="/import" element={<Import />} />
          <Route path="/migration" element={<Migration />} />
          <Route path="/logo-demo" element={<LogoDemo />} />
          <Route path="/docs/:fileName" element={<MarkdownPage />} />
          <Route path="/pdf/:objectId" element={<PDFPage />} />
          <Route path="/oauth/google-callback" element={<GoogleOAuthCallback />} />
          <Route path="/oauth/gmail-callback" element={<GmailOAuthCallback />} />
          <Route path="/*" element={
            <div className="app">
              <Sidebar />
              <main className="main-content with-sidebar">
                <Suspense fallback={<div style={{ padding: '2rem', color: 'var(--text-secondary, #94a3b8)' }}>Loading…</div>}>
                  <Routes>
                    <Route path="/" element={<Navigate to="/daily" replace />} />
                    <Route path="/object/:id" element={<ObjectView />} />
                    <Route path="/processing" element={<ProcessingCenter />} />
                    <Route path="/daily" element={<DailyNotes />} />
                    <Route path="/calendar" element={<CalendarPage />} />
                    <Route path="/tasks" element={<AllTasks />} />
                    <Route path="/projects" element={<AllProjects />} />
                    <Route path="/maps" element={<AllMaps />} />
                    <Route path="/areas" element={<AllAreas />} />
                    <Route path="/notes" element={<AllNotes />} />
                    <Route path="/sources" element={<AllSources />} />
                    <Route path="/reading-list" element={<ReadingList />} />
                    <Route path="/archived" element={<AllArchive />} />
                    {/* Redirect old routes */}
                    <Route path="/archive" element={<Navigate to="/archived" replace />} />
                    <Route path="/resources" element={<Navigate to="/lists" replace />} />
                    <Route path="/collections" element={<Navigate to="/lists" replace />} />
                    <Route path="/tags" element={<TagsView />} />
                    <Route path="/lists" element={<CollectionsPage />} />
                    <Route path="/habits" element={<HabitsPage />} />
                    <Route path="/all-objects" element={<AllObjects />} />
                    <Route path="/ai-search" element={<AISearch />} />
                    <Route path="/chat" element={<Chat />} />
                    <Route path="/mdm" element={<Mdm />} />
                    <Route path="/mdm/level" element={<MdmLevel />} />
                    <Route path="/graph" element={<GraphView />} />
                  </Routes>
                </Suspense>
              </main>
              <Footer />
              <CelebrationOverlay />
              {openObjectId && (
                <ModalRenderer
                  objectId={openObjectId}
                  onClose={closeObjectModal}
                />
              )}
            </div>
          } />
        </Routes>
      </Suspense>
    </Router>
  );
}

export default App;
